import type { PaginatedResponse } from './types';

/**
 * Fetches all pages of paginated data by following the lastKey until all data is retrieved
 */
export async function fetchAllPages<T>(
  fetchPage: (params: { limit?: number; lastKey?: string }) => Promise<PaginatedResponse<T>>,
  options?: {
    limit?: number;
    maxPages?: number; // Safety limit to prevent infinite loops
  }
): Promise<T[]> {
  // Use a larger limit to reduce the number of API calls
  const limit = options?.limit || 500;
  const maxPages = options?.maxPages || 20; // Reduced safety limit to prevent excessive calls
  const allItems: T[] = [];
  let lastKey: string | null | undefined = undefined;
  let pageCount = 0;
  let total: number | null | undefined = undefined;
  const seenKeys = new Set<string>(); // Track seen keys to detect loops

  do {
    // Safety check to prevent infinite loops
    if (pageCount >= maxPages) {
      break;
    }

    const response = await fetchPage({
      limit,
      lastKey: lastKey || undefined,
    });

    // Store total from first page
    if (pageCount === 0 && response.total !== undefined && response.total !== null) {
      total = response.total;
    }

    // If we got no items, we're done
    if (response.items.length === 0) {
      break;
    }

    // Check for duplicate lastKey (infinite loop detection)
    if (lastKey && seenKeys.has(lastKey)) {
      console.warn(`Detected duplicate lastKey, stopping pagination to prevent infinite loop. Total items: ${allItems.length}`);
      break;
    }
    if (lastKey) {
      seenKeys.add(lastKey);
    }

    allItems.push(...response.items);
    const previousLastKey: string | null | undefined = lastKey;
    lastKey = response.lastKey;
    pageCount++;

    // If we have total count, check if we've fetched all items
    if (total !== null && total !== undefined) {
      if (allItems.length >= total) {
        break;
      }
    }

    // Stop if lastKey is null, undefined, or empty string (no more pages)
    // This is the primary stopping condition
    if (lastKey === null || lastKey === undefined || lastKey === '') {
      break;
    }

    // Additional safety: if we got fewer items than requested AND lastKey is the same, stop
    if (response.items.length < limit && previousLastKey === lastKey) {
      break;
    }
  } while (true);

  return allItems;
}

